import { format } from "date-fns";

/**
 * Formats a date object as a string, optionally including the time and using shortened month names.
 *
 * @param {Date} date - The date object to format.
 * @param {boolean} [includeTime=false] - Whether to include the time in the output. Defaults to false.
 * @param {boolean} [shorten=false] - Whether to use shortened month names in the output. Defaults to false.
 * @returns {string} A string representing the input date in the format "DD Month YYYY" or "DD Month YYYY at HH:MM" if includeTime is true.
 */
export const dateFormatter = (
  date: Date | number,
  includeTime: boolean = false,
  shorten: boolean = false,
): string => {
  let newDate: Date;
  if (typeof date === "number") {
    newDate = new Date(date);
  } else {
    newDate = date;
  }

  const year = newDate.getFullYear();
  const day = newDate.getDate();
  const monthFormat = shorten ? "MMM" : "MMMM";
  const month = format(date, monthFormat);
  const formattedDate = `${day} ${month} ${year}`;

  if (includeTime) {
    const formattedTime = format(date, "HH:mm");
    return `${formattedDate} at ${formattedTime}`;
  }

  return formattedDate;
};

/**
 * Returns the current date in a specific format based on the platform.
 *
 * @param {string} platform - A string indicating the platform for which the date should be formatted. Valid values are "mobile" and "desktop".
 * @returns {string} A string representing the current date in the format specified by the platform argument.
 */
export function getDate(platform: string): string {
  const dateObj = new Date();
  const day = dateObj.getDate();

  if (platform === "mobile") {
    const shortMonth = format(dateObj, "MMM");
    return `${day} ${shortMonth}`;
  }

  if (platform === "desktop") {
    const longMonth = format(dateObj, "MMMM");
    const year = dateObj.getFullYear();
    return `${day} ${longMonth} ${year}`;
  }

  return "";
}

/**
 * Formats a date object as a string in 12-hour time with am/pm.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} A string representing the input date in the format "h:mm am/pm".
 */
export const timeFormatter = (date: Date): string => {
  // Use 'format' to format the time as a string in the desired format
  const formattedTime = format(date, "h:mm a").toLocaleLowerCase(); // E.G. 12:00 am

  return formattedTime;
};

/**
 * Formats a timestamp as a string in the format "DD/MM/YY".
 *
 * @param {number} timestamp - The timestamp to format.
 * @param {boolean} fullDate - Whether to include the full year in the output. Defaults to false.
 * @returns {string} A string representing the input timestamp in the format "DD/MM/YY" or "DD/MM/YYYY" if fullDate is true.
 */
export const timestampFormatter = (timestamp: number, fullDate?: boolean): string => {
  const date = new Date(timestamp);

  // Determine the format based on the fullDate parameter
  const dateFormat = fullDate ? "dd/MM/yyyy" : "dd/MM/yy";

  // Use 'format' to format the date as a string in the desired format
  const formattedDate = format(date, dateFormat);

  return formattedDate;
};

/**
 * Get the short form of the month from a given date string.
 * @param {string} dateString - The date string in ISO format.
 * @returns {string} The short form of the month (e.g., "Jan", "Feb", etc.).
 */
export const getShortMonthFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const shortMonth = new Intl.DateTimeFormat("en", { month: "short" }).format(date);
  return shortMonth;
};
